// The Penyedia templates inside a paket's stage sections (Simulasi Pluto v4
// plutoDocButtons): a grid of document cards, each with Unduh Word (the
// skeleton filled from this paket and its work tools, no Pluto turn), Buat
// dokumen and Dengan arahan (the draft opens in the right panel like a
// checklist step's, ../ActivityDocs.tsx, as the stage's last activity). Sanggah shows only for
// Tender/Seleksi, sanggah banding only for a Tender Pekerjaan Konstruksi.
import * as API from "../../api";
import type { Project } from "../../api";
import { canDocx, useDocx } from "../../pbj/docx";
import { ActivityDocButtons, ActivityDocStatus, canActivityDocs, templateKey } from "../ActivityDocs";
import { useFeatures } from "../../shell/features";
import { Glyph } from "../../ui/icons";

type Paket = Pick<Project, "metode" | "jenisPengadaan">;

/** Whether a Penyedia template applies to the paket (an unknown metode or jenis keeps it, with its note). */
export function templateApplies(t: Pick<API.TemplateItem, "category">, paket: Paket): boolean {
  if (t.category === "sanggah") return !paket.metode || paket.metode === "tender_seleksi";
  if (t.category === "banding") {
    return (!paket.metode || paket.metode === "tender_seleksi") && (!paket.jenisPengadaan || paket.jenisPengadaan === "pekerjaan_konstruksi");
  }
  return true;
}

/** The templates of a stage by category, in library order. */
export function stageGroups(templates: API.TemplateItem[], stage: string, paket: Paket): { label: string; items: API.TemplateItem[] }[] {
  const out: { label: string; items: API.TemplateItem[] }[] = [];
  for (const t of templates) {
    if (t.stage !== stage || !templateApplies(t, paket)) continue;
    const group = out.find((g) => g.label === t.categoryLabel);
    if (group) group.items.push(t);
    else out.push({ label: t.categoryLabel, items: [t] });
  }
  return out;
}

/** The stage's templates of the given categories (all when none), applicable to the paket, at most `max`. */
export function stageTemplates(templates: API.TemplateItem[], stage: string, paket: Paket, categories: string[] = [], max = 12): API.TemplateItem[] {
  return templates.filter((t) => t.stage === stage && (categories.length === 0 || categories.includes(t.category)) && templateApplies(t, paket)).slice(0, max);
}

export interface DocGridProps {
  projectId: string;
  templates: API.TemplateItem[];
  /** The grid's accessible name. */
  label: string;
}

export function DocGrid({ projectId, templates, label }: DocGridProps) {
  const features = useFeatures();
  const ready = features.list !== null;
  const docxOn = ready && canDocx(features);
  const draftOn = canActivityDocs(features);
  const docx = useDocx();
  if (templates.length === 0 || (!docxOn && !draftOn)) return null;
  return (
    <ul className="pks-docs" aria-label={label}>
      {templates.map((t) => (
        <li key={t.id} className="pks-doc">
          <span className="pks-doc-text">
            <span className="pks-doc-title">
              <Glyph name="documentText" size={18} />
              <span>{t.title}</span>
            </span>
            <span className="pks-doc-desc">{t.description}</span>
            {t.note ? <span className="pks-doc-desc">{t.note}</span> : null}
            <ActivityDocStatus activityKey={templateKey(t.id)} title={t.title} />
            {docx.error?.key === t.id ? (
              <span className="pk-item-error" role="alert">
                <Glyph name="danger" size={16} />
                <span>{docx.error.message}</span>
              </span>
            ) : null}
          </span>
          <span className="pks-doc-actions">
            {docxOn ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={docx.busy !== null}
                aria-busy={docx.busy === t.id || undefined}
                aria-label={`Unduh Word ${t.title}`}
                onClick={() => void docx.start(t.id, () => API.downloadTemplateDocx(t.id, projectId))}
              >
                <Glyph name="documentDownload" />
                <span>{docx.busy === t.id ? "Menyiapkan" : "Unduh Word"}</span>
              </button>
            ) : null}
            {draftOn ? <ActivityDocButtons activityKey={templateKey(t.id)} title={t.title} variant="card" /> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
